import React from 'react';
import cn from 'classnames';
import Icon from '../icon';
import { Visibility } from '../../../types/common-types';
import changeLocation from '../../../utils/changeLocation';
import openWindow from '../../../utils/openWindow';
import { bbCodeToHtml } from '../../../utils/formUtils';
import './style.scss';

export enum ButtonType {
  Submit = 'submit',
  Normal = 'normal',
  Open = 'open',
  Secondary = 'secondary',
  Link = 'link',
  Redirect = 'redirect',
  // eslint-disable-next-line no-shadow
  Icon = 'icon',
  Image = 'image',
}

export enum IconSize {
  Medium = 'medium',
  Small = 'small',
}

export enum IconAlign {
  Top = 'top',
  Left = 'left',
}

interface Props {
  id?: string;
  label: string;
  submiting?: boolean;
  error?: boolean;
  type: ButtonType;
  visibility?: Visibility;
  className?: string;
  style?: object;
  extra?: {
    url?: string;
    imgSrc?: string;
    imgAlt?: string;
    icon?: string;
    iconAlign?: string;
    iconSize?: string;
  };
  styleName?: string;
  onClick: (e: React.MouseEvent) => void;
  onKeyPress?: (e: React.KeyboardEvent) => void;
}

const Button: React.FC<Props> = ({
  id,
  label,
  type = ButtonType.Submit,
  visibility = Visibility.Visible,
  onClick,
  submiting = false,
  error = false,
  extra = {},
  onKeyPress,
  className,
  style,
}) => {
  return (
    <div
      id={id}
      style={type === ButtonType.Image ? {} : style}
      className={className}
      styleName={cn('button', {
        submit: type === ButtonType.Submit || type === ButtonType.Redirect || type === ButtonType.Open,
        normal: type === ButtonType.Normal,
        link: type === ButtonType.Link,
        icon: type === ButtonType.Icon,
        image: type === ButtonType.Image,
        secondary: type === ButtonType.Secondary,
        disabled: visibility === Visibility.Disabled,
        hidden: visibility === Visibility.Hidden,
        leftIcon: extra.iconAlign === IconAlign.Left,
        smallIcon: extra.iconSize === IconSize.Small,
        error,
      })}
      role="button"
      data-cy="button"
      data-cy-redirect-url={type === ButtonType.Redirect ? extra.url : ''}
      tabIndex={visibility === Visibility.Visible ? 0 : -1}
      onClick={e => {
        if (visibility === Visibility.Visible && !submiting) {
          if (type === ButtonType.Redirect) {
            if (extra.url) {
              changeLocation(extra.url);
            }
          }
        else if (type === ButtonType.Open) {
            if (extra.url) {
              openWindow(extra.url);
            }
          } else {
            onClick(e);
          }
        }
      }}
      onKeyPress={e => {
        if (onKeyPress) {
          onKeyPress(e);
        }
      }}
    >
      {type === ButtonType.Icon && extra.icon ? (
        <div className="icon" styleName="button__icon">
          <Icon value={extra.icon} />
        </div>
      ) : null}
      {type === ButtonType.Image && extra.imgSrc ? (
        <img style={style} alt={extra.imgAlt} src={extra.imgSrc} />
      ) : null}
      {submiting ? (
        <div styleName="button__spinner__wrap">
          <div styleName="button__spinner">
            <div styleName="bar1" />
            <div styleName="bar2" />
            <div styleName="bar3" />
            <div styleName="bar4" />
            <div styleName="bar5" />
            <div styleName="bar6" />
            <div styleName="bar7" />
            <div styleName="bar8" />
            <div styleName="bar9" />
            <div styleName="bar10" />
            <div styleName="bar11" />
            <div styleName="bar12" />
          </div>
        </div>
      ) : null}
      <span
        styleName={cn('button__label', {
          hidden: submiting,
          icon: type === ButtonType.Icon,
        })}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: bbCodeToHtml(label),
        }}
      />
    </div>
  );
};

export default Button;